// Server-authoritative reveal read/write path (hidden-info domain R2d).
//
// Every reveal mechanic (face-down flip, scry, tutor/search, unitHands `seenBy`)
// becomes an authoritative SET_REVEALED mutation instead of shipping the card
// identity over a P2P channel. The actor mutates LOCALLY first (the card is
// already flipped/known on its own screen), THEN emits SET_REVEALED; the server
// records the reveal on `cards[id].revealedTo` / `faceDown` and the per-seat
// projection un-strips the identity for the entitled seat ONLY. The opponent
// learns the identity through the projected diff. Never a P2P reveal payload.
//
// This mapper folds those reveals onto the OPPONENT's card meshes: when the
// mirror now carries an identity for an opponent-owned card the local mesh still
// shows blank (a skeleton), it re-hydrates name/cardId/imageUrl, repaints the
// front texture, and remembers the identity in the vault. It also mirrors the
// authoritative `faceDown` flag so a flip animates on the opponent's screen.

import { SET_REVEALED } from '../../../utils/game/net/matchCore';
import { updateCardFrontTexture } from '../../../utils/game/cardMesh';
import { syncCardFace } from '../../../utils/game/engine/core/cardFace';
import { animateCardFlip } from '../../../utils/game/animations';
import { playSound } from '../../../utils/game/sounds';

// Build a directed reveal command. `seats` is the list of seats that may now see
// the card identity (the opponent seat for a face-up flip); `faceDown`, when
// given, sets the authoritative flag so the flip mirrors. revealedTo is sticky
// (a later face-down keeps the reveal, matching the legacy behavior where the
// opponent retained a flipped card's name once seen).
export function revealCommand(cardId, seats, faceDown) {
  const payload = { cardId };
  if (Array.isArray(seats)) payload.seats = seats;
  if (faceDown !== undefined) payload.faceDown = faceDown;
  return { type: SET_REVEALED, payload };
}

// Write a newly-learned identity onto an opponent-owned mesh that is still a
// blank skeleton: repaint the front texture, cache the identity so a later
// sanitized bounce re-hydrates instead of going blank, and let the logbook name
// the card from here on (sticky).
//
// The authoritative registry stores only `cardId`. Name and art are never on
// the wire. So the face is resolved from the local catalog, the same way
// applyHandFromMirror rebuilds this seat's own hand on a reconnect. A mirror
// entry that DOES carry a name (a masked Imposter/Dragonlord identity, pushed
// via SET_IDENTITY) wins over the catalog, since the catalog would resolve the
// card's true printing and undo the mask.
//
// Idempotent. A mesh that already carries a name is left alone. Returns true
// iff something changed.
export function foldRevealedIdentity(host, mesh, card, mc) {
  if (card.name) return false;
  const full = mc.cardId != null ? host._getFullCard?.(mc.cardId) : null;
  const name = mc.name ?? full?.name;
  if (!name) return false;

  card.name = name;
  if (mc.cardId != null) card.cardId = mc.cardId;
  if (mc.foiling != null) card.foiling = mc.foiling;
  const imageUrl = mc.imageUrl ?? full?.printings?.[0]?.image_url;
  if (imageUrl) {
    card.imageUrl = imageUrl;
    updateCardFrontTexture(mesh, imageUrl);
  }
  host.identityVault?.remember?.(card);
  card.nameKnown = true;
  return true;
}

// Fold authoritative reveals from the mirror onto the OPPONENT's card meshes.
// Idempotent: a card whose local mesh already carries the mirror's identity (the
// actor's own reveal, or a redundant fold) is a no-op. Skips the local seat
// entirely (single-writer. Its own cards always carry full identity locally).
export function applyRevealsFromMirror(host, mirrorState) {
  // Solo: the pilot sees every identity already (merged projection). Nothing
  // is ever skeletonized, so the identity re-hydration below is a no-op; the
  // face-down flag mirror still applies.
  const cards = mirrorState?.cards;
  if (!cards) return;

  const localSeat = host.props?.isCasualSandbox ? null : (host.state?.isHost ? 'p1' : 'p2');
  let aurasDirty = false;

  for (const [instanceId, mc] of Object.entries(cards)) {
    if (!mc || mc.owner === localSeat) continue;
    const mesh = host.meshes?.get(instanceId);
    if (!mesh) continue;
    const card = mesh.userData?.cardInstance;
    if (!card) continue;

    // The mirror reveals identity by un-stripping the card (cardId/name appear).
    // A skeleton (no cardId) means the seat is not entitled. Nothing to fold.
    const revealedIdentity = mc.cardId != null || mc.name != null;
    if (revealedIdentity && foldRevealedIdentity(host, mesh, card, mc)) {
      aurasDirty = true;
    }

    // A card the mirror still strips to a skeleton is one this seat may not
    // identify: it is in the actor's hand, mid-drag, and our copy is masked
    // face-down on purpose (syncBridge.placeCard). The server meanwhile
    // projects the card's real `faceDown: false` from the hand. Mirroring
    // that would strip the mask and show a blank card face.
    //
    // Gate on the projection, NOT on drag ownership. The authoritative diff
    // beats the cosmetic `card:release` relay, so an ownership-based guard
    // skipped the very diff that revealed the card and left every play
    // un-flipped until the NEXT action produced another diff. Gating here
    // means the same diff that un-strips the card also releases the flip.
    //
    // A skeleton that is genuinely face-down still mirrors: that is a real
    // face-down board card being hidden, not our drag mask.
    if (!revealedIdentity && mc.faceDown === false) continue;

    // Mirror the authoritative face-down flag (a flip animates on the opponent's
    // screen). Only act on an actual change so the fold stays idempotent.
    if (typeof mc.faceDown === 'boolean' && card.faceDown !== mc.faceDown) {
      card.faceDown = mc.faceDown;
      syncCardFace(card);
      animateCardFlip(mesh, card);
      playSound('cardFlip');
      aurasDirty = true;
    }
  }

  // Revealing a face-down card can introduce a fresh aura source (Bannerman,
  // King, etc.); hiding one removes it.
  if (aurasDirty) host.recomputeStaticAuras?.();
}
